import { resetWidget } from "../Components/WidgetHeader/WidgetHeader";
import { HttpClientXHR } from "../Services/http.clients/config/http.client";
import { useAppViewStore } from "../Stores/AppView";
import { useCaptureStore } from "../Stores/Capture";
import { useChatbarNotificationStore } from "../Stores/ChatbarNotification";
import { useChecklistStore } from "../Stores/Checklist";
import { useConfigStore } from "../Stores/Config";
import { useFeatureRequestStore } from "../Stores/FeatureRequests";
import { useFormStore } from "../Stores/Form";
import { useFrontendToolStore } from "../Stores/FrontendTools";
import { useHelpCenterStore } from "../Stores/HelpCenter";
import { useNewsStore } from "../Stores/News";
import { AppMode, useRouterStore } from "../Stores/Router";
import { useSessionStore } from "../Stores/Session";
import { useStatusPageStore } from "../Stores/StatusPage";
import { consumeLegacySurveyAck, useSurveyV2Store } from "../Stores/SurveyV2";
import { useTicketStore } from "../Stores/Ticket";
import { useWebSocketStore } from "../Stores/WebSocket";
import { useWidgetSizeStore } from "../Stores/WidgetSize";
import { clearGleapCache } from "./CacheReader";
import Communicator from "./Communicator";
import {
  hostTargetOrigin,
  isFromAppFrame,
  isFromHost,
  isValidApiUrl,
  isValidRealtimeHost,
} from "./MessageTrust";
import { resolveWidgetApiBase } from "./RegionHosts";
import type { SurveyStartOptions } from "./surveyTypes";
import { handleSalesDemoWidgetMessage, isSalesDemoMode } from "../salesDemo/protocol";

const FORM_SENT_TIMEOUT = 3000;
// App modes in which the frame shows a survey and nothing else. Resolve the
// enum when called, not while this module loads: Router imports Communicator,
// which imports this manager, so eager enum access breaks Vite's module cycle.
const isSurveyAppMode = (mode: AppMode) =>
  mode === AppMode.SURVEY ||
  mode === AppMode.SURVEY_FULL ||
  mode === AppMode.SURVEY_WEB ||
  mode === AppMode.SURVEY_PAGE;
const DEFAULT_RESOLVER_TIMEOUT = 800;
var closeOnSendTimeout: any = null;

const createRecaptcha = function () {
  let script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("defer", "");
  script.id = "recaptchaScript";
  script.src =
    "https://www.recaptcha.net/recaptcha/api.js?render=6LeMpiwcAAAAAAuGag4PWJvwSSgH0mCVX7EDQIjT";
  document.getElementsByTagName("head")[0].appendChild(script);
};

const applySafeArea = (insets: any) => {
  try {
    const root = document.documentElement;
    const px = (value: unknown) =>
      `${Math.max(0, typeof value === "number" ? value : 0)}px`;
    root.style.setProperty("--safe-area-top", px(insets.top));
    root.style.setProperty("--safe-area-right", px(insets.right));
    root.style.setProperty("--safe-area-bottom", px(insets.bottom));
    root.style.setProperty("--safe-area-left", px(insets.left));
  } catch (exp) {}
};

/**
 * `sheet-viewport` (public/appnew.html, card surveys): the room the floating
 * sheet has right now and whether the keyboard is open. The sheet caps itself
 * to it (header and actions stay put, the question scrolls) so the host's
 * container never has to clip the survey.
 */
const applySheetViewport = (viewport: any) => {
  try {
    const root = document.documentElement;
    const maxHeight =
      typeof viewport?.maxHeight === "number" && viewport.maxHeight > 0
        ? Math.floor(viewport.maxHeight)
        : 0;
    if (maxHeight) {
      root.style.setProperty("--sv2-sheet-max", `${maxHeight}px`);
    } else {
      root.style.removeProperty("--sv2-sheet-max");
    }
    const keyboard = !!viewport?.keyboard;
    const opened = keyboard && !root.classList.contains("sv2-kb");
    root.classList.toggle("sv2-kb", keyboard);
    if (opened) {
      // Once the sheet has settled above the keyboard, bring the field back.
      setTimeout(() => {
        const active = document.activeElement as HTMLElement | null;
        if (active && active.closest?.(".sv2-body")) {
          try {
            active.scrollIntoView({ block: "nearest" });
          } catch (exp) {}
        }
      }, 120);
    }
  } catch (exp) {}
};

export default class CommunicationManager {
  private static _instance: CommunicationManager;
  private static injectedScript: boolean = false;
  static isWidgetOpen: boolean = false;
  private resolvers = new Map();
  // The API / realtime hosts the first session-update set. Later updates may
  // change the session but never move the widget (and its credentials) to
  // another host.
  private sessionHosts?: { apiUrl: string; realtimeHost?: string };
  /** The host SDK can switch the widget to another session ("adopt-session"). */
  hostCanAdoptSession = false;
  /** A session the widget asked the host to adopt, and the conversation to reopen once it arrives. */
  pendingAdoption?: { gleapId: string; shareToken?: string };

  static getInstance() {
    if (!this._instance) {
      this._instance = new CommunicationManager();
    }
    return this._instance;
  }

  constructor() {
    this.startMessageListener();
  }

  sendMessageWithResolver(
    message: { name: string; data?: any },
    timeoutMs: number = DEFAULT_RESOLVER_TIMEOUT,
  ) {
    return new Promise((resolve, reject) => {
      // Store the resolve function in the map
      this.resolvers.set(message.name, resolve);

      this.sendMessage(message);

      // Set a timeout for the message. Only clear the entry if it is still ours:
      // resolvers are keyed by message name, so a second in-flight call under the
      // same name replaces this one, and deleting it blindly would strand the
      // newer caller until its own timeout. Overlap is unlikely but the window
      // scales with the timeout, and app mode now uses a much longer one.
      setTimeout(() => {
        if (this.resolvers.get(message.name) === resolve) {
          this.resolvers.delete(message.name);
        }
        reject(new Error("Message timeout"));
      }, timeoutMs);
    });
  }

  sendMessage(data: any) {
    if (isSalesDemoMode()) {
      handleSalesDemoWidgetMessage(data);
      return;
    }
    if (data && data.name === "close-widget" && closeOnSendTimeout) {
      clearTimeout(closeOnSendTimeout);
      closeOnSendTimeout = null;
    }

    var parentWindow = (window as any).top;
    if ((window as any).parent != (window as any).top) {
      parentWindow = (window as any).parent;
    }

    if (parentWindow) {
      // Pinned to the host's origin whenever it is known (same-origin
      // about:blank frame, native shell page); '*' only as a last resort.
      parentWindow.postMessage(
        JSON.stringify(data),
        hostTargetOrigin(parentWindow),
      );
    }
  }

  applyStartPage() {
    const routerStore = useRouterStore.getState();
    const configStore = useConfigStore.getState();
    const startPage = configStore.config?.startPage;

    // Only apply start page when on the home page (not deep-linked).
    if (routerStore.currentPage !== "menu") {
      return;
    }

    if (!startPage || startPage === "home") {
      return;
    }

    // Show loading spinner instead of home page while navigating.
    routerStore.setStartPageLoading(true);

    if (startPage === "latestConversation") {
      const navigateToLatest = (state: ReturnType<typeof useTicketStore.getState>) => {
        const latestOpen = state.previousTickets.find(
          (ticket) => ticket.status !== "DONE"
        );
        if (latestOpen && useRouterStore.getState().currentPage === "menu") {
          useRouterStore.getState().setPage("conversation", {
            shareToken: latestOpen.shareToken,
          });
        }
        useRouterStore.getState().setStartPageLoading(false);
      };

      const current = useTicketStore.getState();
      if (current.previousTicketsLoaded) {
        navigateToLatest(current);
        return;
      }

      // Conversations load asynchronously — wait for the first load to settle.
      const unsubscribe = useTicketStore.subscribe((state) => {
        if (state.previousTicketsLoaded) {
          unsubscribe();
          navigateToLatest(state);
        }
      });

      // Safety net: clean up subscription after 10 seconds if conversations never load.
      setTimeout(() => {
        unsubscribe();
        useRouterStore.getState().setStartPageLoading(false);
      }, 10000);
      return;
    }

    // Direct page mapping for: conversations, news, helpcenter, roadmap.
    routerStore.setPage(startPage);
    routerStore.setStartPageLoading(false);
  }

  reloadMainData() {
    useTicketStore.getState().silentRefreshTickets();

    if (useTicketStore.getState().currentTicket) {
      try {
        useTicketStore.setState({
          botWrittingText: undefined,
        });
        useTicketStore.getState().loadComments(true);
      } catch (exp) {}
    }
  }

  reconnect() {
    useWebSocketStore.getState().connect(() => {
      this.reloadMainData();
    });
  }

  disconnect() {
    useWebSocketStore.getState().disconnect();
  }

  /**
   * Wipe every session-bound in-memory store and disconnect the WebSocket.
   *
   * Called when the parent SDK signals a session change (`session-update` with a new
   * gleapId, or `session-cleared`). The iframe's Zustand stores hold per-user state
   * (`currentTicket`, comments, form drafts, current page, news feed, feature
   * requests, status page snapshot, checklists, help-center cache) that must NOT
   * survive into the next user's session — otherwise a second user on the same
   * browser inherits the previous user's `currentTicket.shareToken` and the
   * messenger polls it with the new auth headers.
   *
   * The WebSocket is also closed: Pusher channelAuthorization captures
   * gleap-id/gleap-hash at connect time, so the live connection keeps the previous
   * user's `private-session-${gleapId}` subscription open until reconnect.
   */
  resetForSessionChange(newSessionFollows = false) {
    const surveyAction = this.surveyActionOnSessionChange(newSessionFollows);
    try {
      useTicketStore.getState().clearCurrentTicket();
      useTicketStore.setState({
        previousTickets: [],
        previousTicketsLoaded: false,
        unreadTickets: [],
        ticketFilter: {},
        currentComments: [],
      });
    } catch (exp) {}
    try {
      useFormStore.getState().resetForm();
    } catch (exp) {}
    try {
      useSurveyV2Store.getState().reset();
    } catch (exp) {}
    try {
      useRouterStore.getState().reset();
    } catch (exp) {}
    try {
      useAppViewStore.getState().reset();
    } catch (exp) {}
    try {
      useFeatureRequestStore.setState({
        currentFeatureRequest: undefined,
        currentComments: [],
        featureRequests: [],
        featureListSkip: 0,
        hasMoreFeatures: true,
        upvotes: [],
      });
    } catch (exp) {}
    try {
      useChecklistStore.setState({ checklists: [] });
    } catch (exp) {}
    try {
      // Capture requests belong to the previous user's conversations: close
      // an open annotation editor and drop per-request state.
      useCaptureStore.getState().reset();
    } catch (exp) {}
    try {
      useWebSocketStore.getState().disconnect();
    } catch (exp) {}
    this.applySurveyActionOnSessionChange(surveyAction);
  }

  /** The `start-survey` data of the survey on screen, to start it again for a new session. */
  private lastSurveyStart: { surveyId: string; data: any } | null = null;

  /**
   * An in-app survey (card / full screen started by the host, not a survey
   * page or a link inside the messenger) open while the identity changes:
   * the reset tears it down, so the host must hear about it or a native SDK
   * keeps an empty, possibly dimmed frame. Not answered yet (loading, welcome,
   * first question) → start it again for the new session, so "Skip when known"
   * is decided for the new contact; otherwise → close it.
   */
  private surveyActionOnSessionChange(
    newSessionFollows: boolean,
  ): { type: "restart"; data: any } | { type: "close"; data: any } | null {
    try {
      const survey = useSurveyV2Store.getState();
      if (useRouterStore.getState().currentPage !== "survey") return null;
      if (survey.phase === "idle" || survey.closed || !survey.surveyId) return null;
      if (survey.format === "page" || survey.options?.inMessenger) return null;
      const untouched =
        !survey.endingId &&
        !survey.answeredOnce &&
        Object.keys(survey.answers).length === 0;
      const start = this.lastSurveyStart;
      if (untouched && newSessionFollows && start?.surveyId === survey.surveyId) {
        return { type: "restart", data: start.data };
      }
      return {
        type: "close",
        data: {
          surveyId: survey.surveyId,
          version: survey.responseVersion || survey.payload?.version,
          responseId: survey.responseId || undefined,
          status: survey.endingId
            ? "completed"
            : Object.keys(survey.answers).length > 0
            ? "in_progress"
            : "not_started",
          reason: "session_changed",
        },
      };
    } catch (exp) {
      return null;
    }
  }

  private applySurveyActionOnSessionChange(
    action: ReturnType<CommunicationManager["surveyActionOnSessionChange"]>,
  ) {
    if (!action) return;
    if (action.type === "restart") {
      // After the caller has stored the new session (session-update sets it
      // right after this reset).
      setTimeout(() => this.startSurvey(action.data), 0);
      return;
    }
    this.lastSurveyStart = null;
    this.sendMessage({ name: "survey-closed", data: action.data });
    Communicator.closeWidget();
  }

  /**
   * `start-survey` (spec §6): try the Surveys 2.0 definition first; a 404 or
   * any error falls back to the legacy FeedbackFlow exactly as before.
   *
   * Accepts the legacy shape `{ flow, format: 'survey'|'survey_full'|'survey_web' }`
   * and the JS SDK's options `{ flow, format: 'card'|'full'|'page', fields,
   * personalToken, resume, channel, source, pageUrl }` (also nested under
   * `options`, or passed as the `format` object itself).
   */
  startSurvey(startData: any) {
    this.lastSurveyStart = null;
    const nested =
      startData.format && typeof startData.format === "object"
        ? startData.format
        : startData.options && typeof startData.options === "object"
        ? startData.options
        : {};
    const pick = (key: string) =>
      startData[key] !== undefined && typeof startData[key] !== "object"
        ? startData[key]
        : nested[key] ?? startData[key];
    const surveyId: string = startData.flow ?? startData.surveyId ?? nested.surveyId;
    const rawFormat: string | undefined =
      typeof startData.format === "string" ? startData.format : nested.format;

    // JS SDK ≥ Surveys 2.0 sends the app mode in `format` and the survey
    // format separately in `surveyFormat`; older hosts only send `format`.
    const surveyFormat: string | undefined =
      startData.surveyFormat ?? nested.surveyFormat;
    const v2Format: SurveyStartOptions["format"] =
      surveyFormat === "card" || surveyFormat === "full" || surveyFormat === "page"
        ? surveyFormat
        : rawFormat === "page" || rawFormat === "survey_page"
        ? "page"
        : rawFormat === "full" ||
          rawFormat === "survey_full" ||
          rawFormat === "survey_web"
        ? "full"
        : rawFormat === "card" || rawFormat === "survey"
        ? "card"
        : undefined;
    // What the legacy flow received before Surveys 2.0 (raw legacy values
    // pass through untouched; the new names map onto their legacy modes).
    const legacyMode: any =
      rawFormat === "card"
        ? AppMode.SURVEY
        : rawFormat === "full"
        ? AppMode.SURVEY_FULL
        : rawFormat === "page" || rawFormat === "survey_page"
        ? AppMode.SURVEY_WEB
        : rawFormat;

    const fields =
      (startData.fields && typeof startData.fields === "object"
        ? startData.fields
        : nested.fields) ?? undefined;

    const options: SurveyStartOptions = {
      surveyId,
      format: v2Format,
      fields,
      personalToken: pick("personalToken"),
      resume: !!pick("resume"),
      resumeData:
        startData.resumeData && typeof startData.resumeData === "object"
          ? startData.resumeData
          : nested.resumeData,
      channel: pick("channel"),
      source:
        startData.source ??
        nested.source ??
        (startData.outboundAction
          ? { outboundAction: startData.outboundAction }
          : undefined),
      prefill: pick("prefill") ?? fields?.__prefill,
      legacyMode,
    };
    if (pick("pageUrl")) (options as any).pageUrl = pick("pageUrl");
    if (startData.safeArea) applySafeArea(startData.safeArea);
    if (pick("sheet")) (options as any).sheet = true;

    const startLegacy = () => {
      useFormStore.setState({
        feedbackFlow: surveyId,
        isSurveyFlow: true,
      });
      useRouterStore.getState().setPage(
        "flow",
        {
          flowId: surveyId,
        },
        legacyMode,
      );
      useAppViewStore.getState().reset();
    };

    if (!surveyId) {
      startLegacy();
      return;
    }

    const appMode =
      v2Format === "page"
        ? AppMode.SURVEY_PAGE
        : v2Format === "full"
        ? legacyMode === AppMode.SURVEY_WEB
          ? AppMode.SURVEY_WEB
          : AppMode.SURVEY_FULL
        : AppMode.SURVEY;
    useRouterStore.getState().setPage("survey", { surveyId }, appMode);
    useAppViewStore.getState().reset();
    this.lastSurveyStart = { surveyId, data: startData };

    useSurveyV2Store
      .getState()
      .load(options)
      .then((result) => {
        const router = useRouterStore.getState();
        if (
          result === "legacy" &&
          router.currentPage === "survey" &&
          router.currentData?.surveyId === surveyId
        ) {
          // Hosts that switched to the v2 chrome optimistically revert to
          // their legacy survey container (appnew.html, JS SDK).
          this.sendMessage({ name: "survey-legacy", data: { surveyId } });
          startLegacy();
        }
      });
  }

  processAction(data: any) {
    if (data.data && data.data.hideBackButton) {
      useRouterStore.setState({
        showBackButton: false,
      });
    }

    if (data.name === "start-feedbackflow") {
      useFormStore.setState({
        feedbackFlow: data.data.flow,
        isSurveyFlow: false,
      });
      useRouterStore.getState().pushPage("flow", {
        flowId: data.data.flow,
      });
      useAppViewStore.getState().reset();
    }
    if (data.name === "start-survey") {
      this.startSurvey(data.data ?? {});
    }
    if (data.name === "start-bot") {
      useTicketStore.getState().createNewConversation({
        botId: data.data.botId,
      });
    }
    if (data.name === "ask-ai") {
      useTicketStore.getState().createNewConversation(
        {
          aiQuestion: true,
        },
        data.data.question ?? "",
      );
    }
    if (data.name === "open-home") {
      useRouterStore.getState().setPage("menu");
    }
    if (data.name === "open-conversations") {
      useRouterStore.getState().pushPage("conversations");

      // Reload.
      useTicketStore.getState().loadConversations();
    }
    if (data.name === "open-conversation") {
      useRouterStore.getState().pushPage("conversation", {
        shareToken: data.data.shareToken,
      });

      // Reload.
      const currentTicket = useTicketStore.getState().currentTicket;
      if (currentTicket) {
        useTicketStore.getState().loadComments(true);
      }
    }
    if (data.name === "open-news-article") {
      useRouterStore.getState().pushPage("newsdetails", {
        id: data.data.id,
      });
    }
    if (data.name === "open-checklists") {
      useRouterStore.getState().setPage("checklists", {
        id: data.data.id,
      });
    }
    if (data.name === "open-checklist") {
      useRouterStore.getState().pushPage("checklistdetails", {
        id: data.data.id,
      });
    }
    if (data.name === "start-checklist") {
      useRouterStore.getState().setPage("checklists");
      useChecklistStore
        .getState()
        .startChecklist(data.data.outboundId, data?.data?.sharedKey);
    }
    if (data.name === "open-news") {
      useRouterStore.getState().setPage("news");
    }
    if (data.name === "open-helpcenter") {
      useRouterStore.getState().setPage("helpcenter");
    }
    if (data.name === "open-helpcenter-search") {
      useRouterStore.getState().pushPage("helpcenter-search", {
        term: data.data.term,
      });
    }
    if (data.name === "open-help-collection") {
      useRouterStore.getState().pushPage("helpcenter-collection", {
        id: data.data.collectionId,
      });
    }
    if (data.name === "open-help-article") {
      useHelpCenterStore.getState().openArticle(data.data.articleId);
    }
    if (data.name === "open-feature-requests") {
      useRouterStore.getState().pushPage("roadmap");
    }
  }

  /** `type: "app"` messages from the app frame the Messenger created. */
  handleAppMessage(data: any) {
    if (data.name === "page") {
      useAppViewStore
        .getState()
        .pushPage(data.data.title, data.data.extendScreen, data.data?.url);
    }
    if (data.name === "pagetitle") {
      useAppViewStore
        .getState()
        .replacePageTitle(
          data.data.title,
          data.data.extendScreen,
          data.data?.url,
        );
    }
    if (data.name === "data") {
      useAppViewStore.getState().setAppData(data.data);
    }
    if (data.name === "open-url") {
      Communicator.openURL(data.data, data.newTab ? true : false, {
        placeholders: false,
      });
    }
    if (data.name === "show-app-scroll-title") {
      useAppViewStore
        .getState()
        .setShowExtendedViewTitle(data.data?.show ? true : false);
    }
  }

  /**
   * session-update from the host: the session plus the hosts the widget
   * talks to. A message without session data is ignored (it used to move the
   * base URL and then throw, leaving the old credentials pointed at the new
   * host). The hosts are taken from the first update only.
   */
  applySessionUpdate(update: any) {
    const sessionData = update?.sessionData;
    if (!sessionData || typeof sessionData !== "object") {
      return;
    }
    if (update?.capabilities?.adoptSession === true) {
      this.hostCanAdoptSession = true;
    }

    if (!this.sessionHosts) {
      if (!isValidApiUrl(update.apiUrl)) {
        return;
      }
      this.sessionHosts = {
        // SDKs are configured with the ingest host (api.*); the widget talks
        // to the dashboard-facing one (dashapi.*) of the SAME region. Custom
        // domains and localhost pass through untouched.
        apiUrl: resolveWidgetApiBase(update.apiUrl),
        // Only the JS SDK sends this (Gleap.setRealtimeHost); native SDKs
        // never do, in which case the realtime host is derived from apiUrl.
        realtimeHost: isValidRealtimeHost(update.realtimeHost)
          ? update.realtimeHost.trim()
          : undefined,
      };
    } else {
      const requestedApiUrl = isValidApiUrl(update.apiUrl)
        ? resolveWidgetApiBase(update.apiUrl)
        : undefined;
      const requestedRealtimeHost = isValidRealtimeHost(update.realtimeHost)
        ? update.realtimeHost.trim()
        : undefined;
      if (
        requestedApiUrl !== this.sessionHosts.apiUrl ||
        requestedRealtimeHost !== this.sessionHosts.realtimeHost
      ) {
        console.warn(
          "[Gleap] Ignoring an API host change after initialization.",
        );
      }
    }
    const { apiUrl, realtimeHost } = this.sessionHosts;

    // Detect identity change BEFORE writing the new session, so we can wipe
    // the previous user's per-session state (currentTicket, comments, form
    // drafts, router page, …) and tear down the WebSocket subscription
    // bound to the old gleapId. Without this, two sequential users on the
    // same browser leak state into each other's session.
    const previousGleapId = useSessionStore.getState().session?.gleapId;
    const newGleapId = sessionData.gleapId;
    const identityChanged =
      !!previousGleapId && previousGleapId !== newGleapId;

    if (identityChanged) {
      this.resetForSessionChange(true);
    }

    // Set the base URL for the HTTP client.
    HttpClientXHR.setBaseURL(apiUrl);

    // Set the session data.
    useSessionStore.setState({
      apiUrl,
      sdkKey: update.sdkKey,
      realtimeHost,
    });

    useSessionStore.getState().setSession(sessionData);

    // A verified email moved the conversation to another contact: the reset
    // above left the conversation, so open it again under the new session.
    const adoption = this.pendingAdoption;
    if (adoption && adoption.gleapId === newGleapId) {
      this.pendingAdoption = undefined;
      if (adoption.shareToken) {
        useRouterStore.getState().setPage("conversation", { shareToken: adoption.shareToken });
      }
    }

    if (identityChanged && CommunicationManager.isWidgetOpen) {
      // The widget was already open when identity flipped — re-subscribe
      // the WebSocket to the new gleapId's channel and refetch the visible
      // surface against the new session.
      this.reconnect();
    }
  }

  startMessageListener() {
    // A sales demo accepts only its origin-bound bridge, never SDK commands.
    if (isSalesDemoMode()) return;
    try {
      // Manage ticket updates when the widget is in the background and then comes back to the foreground.
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          const ticketStore = useTicketStore.getState();
          if (ticketStore.currentTicket?.id) {
            ticketStore.loadComments(true);
          }
        }
      });
    } catch (exp) {}

    // Add window message listener.
    window.addEventListener("message", (event) => {
      try {
        // App frames (AppView) speak `type: "app"`; everything else must come
        // from the host (the SDK page / native shell, or this window itself).
        // Any other sender — an ad frame on the customer's page, a site that
        // opened it in a popup — is ignored: this frame runs in the host's
        // origin and the host SDK trusts whatever it relays.
        const appViewState = useAppViewStore.getState();
        const fromAppFrame = isFromAppFrame(
          event,
          appViewState.appFrame,
          appViewState.currentApp?.url,
        );
        if (!fromAppFrame && !isFromHost(event.source)) {
          return;
        }

        const data = JSON.parse(event.data);

        if (fromAppFrame) {
          if (data?.type === "app") {
            this.handleAppMessage(data);
          }
          return;
        }

        // If the message has a resolver, resolve it.
        const resolve = this.resolvers.get(data.name);
        if (resolve) {
          resolve(data.data);
          this.resolvers.delete(data.name);
        }

        if (data.name === "config-update") {
          if (data.data.config) {
            useConfigStore.getState().setConfig(data.data.config);

            if (
              data.data.config &&
              data.data.config.spamProtection &&
              !CommunicationManager.injectedScript
            ) {
              CommunicationManager.injectedScript = true;
              createRecaptcha();
            }
          }
          if (data.data.isApp) {
            useConfigStore.setState({
              isApp: true,
            });
          }
          if (data.data.surveyCallbacks) {
            useConfigStore.setState({
              hostSurveyCallbacks: true,
            });
          }
          const unifiedTools = data.data.agentTools || data.data.aiTools || [];
          useConfigStore.getState().setAgentTools(unifiedTools);
          if (data.data.overrideLanguage) {
            useConfigStore
              .getState()
              .setOverrideLanguage(data.data.overrideLanguage);
          }
        }
        if (data.name === "widget-size-update") {
          // Expand/collapse window state, owned by the host SDK.
          useWidgetSizeStore.getState().applySizeUpdate(data.data);
        }
        if (
          (data.name === "safe-area-update" ||
            // JS SDK: viewport insets while a full-screen survey is shown.
            data.name === "survey-safe-area") &&
          data.data
        ) {
          // Native shells (iOS/Android SDKs) deliver the device safe-area
          // insets, since env(safe-area-inset-*) is 0 inside our iframe. The
          // layout reads them as CSS variables: headers pad their top by
          // --safe-area-top so a full-screen iPad widget doesn't sit under the
          // status bar. The bottom inset is already applied by the shell
          // (only the Surveys 2.0 renderer pads its own bottom edge).
          applySafeArea(data.data);
        }
        if (data.name === "sheet-viewport") {
          applySheetViewport(data.data);
        }
        // Survey page / inline embed: the host forwards the visitor's keys while
        // focus is on its page (not in this frame), so digits, letters, Enter and
        // arrows still answer the survey like in full screen.
        // Only the host may type: the embedding page (parent) or, in the native
        // shell, this window itself — never another frame on the page.
        const fromHost = isFromHost(event.source);
        if (data.name === "survey-key" && fromHost && data.data && typeof data.data.key === "string" && data.data.key.length <= 16) {
          document.dispatchEvent(
            new KeyboardEvent("keydown", {
              key: data.data.key,
              shiftKey: !!data.data.shiftKey,
              bubbles: true,
              cancelable: true,
            }),
          );
        }
        if (data.name === "reset-routes") {
          useRouterStore.getState().reset();
        }
        if (data.name === "frontend-tool-result") {
          // Result of a Gleap.registerAgentTool handler executed in the host
          // app — deliver it to the awaiting AI agent.
          useFrontendToolStore.getState().handleSdkResult(data.data);
        }
        // Capture requests (host → Messenger). A host that never sends
        // capture-capabilities is treated as legacy: the card offers upload
        // only. Each handler guards itself; a malformed message is ignored.
        if (data.name === "capture-capabilities") {
          try {
            useCaptureStore.getState().applyCapabilities(data.data);
          } catch (exp) {}
        }
        // Native SDKs send the screen they took when the widget opened; Kai
        // intake offers it on its capture card ("Is this the screen?").
        if (data.name === "screenshot-update") {
          try {
            useCaptureStore.getState().setHostScreenshot(data.data);
          } catch (exp) {}
        }
        if (data.name === "capture-state") {
          try {
            useCaptureStore.getState().applyHostState(data.data);
          } catch (exp) {}
        }
        if (data.name === "capture-image") {
          try {
            useCaptureStore.getState().handleCaptureImage(data.data);
          } catch (exp) {}
        }
        if (data.name === "capture-fly-done") {
          try {
            useCaptureStore.getState().finishFlight(data.data?.requestId);
          } catch (exp) {}
        }
        if (data.name === "chatbar-outside-click") {
          // The host SDK listens (non-blocking) for clicks outside the chatbar and
          // posts this. Collapse the open panel.
          useChatbarNotificationStore.getState().requestClose();
        }
        if (data.name === "chatbar-start-agent") {
          // Gleap.startAgent() on the host SDK. Open the chatbar panel and start a
          // fresh conversation with the requested agent (see ChatbarView).
          if (useRouterStore.getState().appMode === AppMode.CHATBAR) {
            useChatbarNotificationStore
              .getState()
              .requestStartAgent(data.data || {});
          }
        }
        if (data.name === "chatbar-notification") {
          // Only show the pill if the chatbar isn't already showing that conversation.
          const current = useTicketStore.getState().currentTicket;
          const sameConversationOpen =
            current && data.data?.shareToken && current.shareToken === data.data.shareToken;
          if (!sameConversationOpen) {
            useChatbarNotificationStore.getState().setPill({
              sender: data.data?.sender,
              text: data.data?.text,
              shareToken: data.data?.shareToken,
            });
          }
        }
        if (data.name === "chatbar-notification-clear") {
          // A sibling tab (relayed by the host SDK) read/dismissed the chatbar
          // pill — hide ours too. Counterpart to `chatbar-notification`; see the
          // host SDK's GleapAiChatbarManager.hideChatbarNotification.
          useChatbarNotificationStore.getState().clear();
        }
        if (data.name === "session-update") {
          this.applySessionUpdate(data.data);
        }
        if (data.name === "widget-status-update") {
          if (useRouterStore.getState().appMode === AppMode.CHATBAR) {
            const wasWidgetOpen = CommunicationManager.isWidgetOpen;
            CommunicationManager.isWidgetOpen = !!data.data.isWidgetOpen;
            if (data.data.isWidgetOpen) {
              // Only (re)connect on the closed→open transition. The chatbar's
              // boot delivers widget-status-update(true) twice (a queued one
              // flushed on ping, then the ping handler's own setPanelOpen), so
              // reconnecting unconditionally opened a second socket.
              if (!wasWidgetOpen) {
                this.reconnect();
              }
              useTicketStore.getState().loadConversations();
              try {
                if (useTicketStore.getState().currentTicket) {
                  // On the closed→open transition, events for this
                  // conversation were dropped while closed — let the refetch
                  // reconcile the bot-turn indicator state from the snapshot.
                  useTicketStore.getState().loadComments(true, !wasWidgetOpen);
                  useTicketStore.getState().refreshCurrentTicket();
                }
              } catch (e) {}
            } else {
              setTimeout(() => {
                if (!CommunicationManager.isWidgetOpen) this.disconnect();
              }, 5000);
            }
            return;
          }
          const wasWidgetOpen = CommunicationManager.isWidgetOpen;
          CommunicationManager.isWidgetOpen = data.data.isWidgetOpen
            ? true
            : false;
          if (data.data.isWidgetOpen) {
            // Track the widget open (opt-in; gated + persisted server-side). Only on
            // the closed→open transition so repeated status updates don't double count.
            if (!wasWidgetOpen) {
              Communicator.trackWidgetEvent("gleap-widget-opened");

              // This message arrives exactly when the SDK reveals the widget
              // (it drops its loading placeholder in the same tick). Bump the
              // open counter so Home + TabBar remount and their entrance
              // choreography replays NOW — on a cold boot the app renders
              // invisibly behind the placeholder, so animations that started
              // at mount time would already be mostly over when revealed.
              useRouterStore.setState((state) => ({
                widgetOpenCount: state.widgetOpenCount + 1,
              }));
            }
            // A survey on its own (card, full screen, survey page) shows none of
            // the home data and no conversation: skip the ~12 home GETs and the
            // realtime socket per survey shown. Closing a survey resets the app
            // to the widget, so the next regular open loads everything.
            const surveyOnly = isSurveyAppMode(
              useRouterStore.getState().appMode
            );
            if (!surveyOnly) {
              this.reconnect();
              useTicketStore.getState().loadConversations();
              useNewsStore.getState().loadNews();
              useFeatureRequestStore.getState().loadRoadmap();
              useStatusPageStore.getState().loadStatusPage();
              useConfigStore.getState().loadTeamInfo();
              useConfigStore.getState().loadHomeWidgets();
              useConfigStore.getState().loadAppPermissions();
              useChecklistStore.getState().loadChecklists();
              useHelpCenterStore.getState().loadTopArticles();
              useHelpCenterStore.getState().loadCollections();

              // Apply configured start page if widget is on the home page.
              this.applyStartPage();

              // Reload current conversation. On the closed→open transition,
              // events for this conversation were dropped while the widget was
              // closed (MESSAGE_CREATED gates on isWidgetOpen) — let the
              // refetch reconcile the bot-turn indicator state from the
              // snapshot it fetches.
              try {
                if (useTicketStore.getState().currentTicket) {
                  useTicketStore.getState().loadComments(true, !wasWidgetOpen);

                  // Reload ticket data silently.
                  useTicketStore.getState().refreshCurrentTicket();
                }
              } catch (exp) {}
            }
          } else {
            // Disconnect from websocket after 5 seconds.
            setTimeout(() => {
              if (!CommunicationManager.isWidgetOpen) {
                this.disconnect();
              }
            }, 5000);

            // Reset when widget is closed in news or app view extended views.
            if (
              useAppViewStore.getState().extendedView ||
              useRouterStore.getState().currentPage === "newsdetails"
            ) {
              resetWidget();
            }

            // Reset show back button.
            useRouterStore.setState({
              showBackButton: true,
            });

            // Check for active survey.
            if (useRouterStore.getState().appMode !== "widget") {
              useRouterStore.getState().setPage("menu");
              useFormStore.getState().resetForm();
              // Flushes any unsaved survey answer (keepalive) and clears it.
              useSurveyV2Store.getState().reset();
            }
          }
        }
        if (data.name === "session-cleared") {
          // Scoped to Gleap-owned keys only. The iframe is same-origin with the
          // host page, so localStorage.clear() here would wipe the host app's
          // storage (e.g. its auth tokens) and log the user out. See
          // clearGleapCache.
          clearGleapCache();
          // Drop every in-memory store the previous identity wrote to, and tear
          // down the WebSocket. Without this the iframe's `currentTicket`, form
          // drafts, router page, and Pusher channel stay bound to the user that
          // just logged out.
          this.resetForSessionChange();
        }
        if (data.name === "prefill-form-data") {
          useFormStore.getState().setPreFillFormData(data.data);
        }
        // A native SDK's reply to the legacy send-feedback of a completed v2
        // survey (see consumeLegacySurveyAck): not for the legacy form.
        const isLegacySurveyAck =
          (data.name === "feedback-sent" ||
            data.name === "feedback-sending-failed") &&
          consumeLegacySurveyAck();
        if (data.name === "feedback-sent" && !isLegacySurveyAck) {
          useFormStore.getState().setFormSent();

          let formSentTimeout =
            useConfigStore.getState().config.thankYouDuration ||
            FORM_SENT_TIMEOUT;

          const formAction = useFormStore.getState().action;
          closeOnSendTimeout = setTimeout(() => {
            closeOnSendTimeout = null;

            if (useRouterStore.getState().appMode === AppMode.SURVEY_WEB) {
              // Don't close the widget in survey web mode.
              return;
            }

            if (
              formAction &&
              formAction.showConversation &&
              data &&
              data.data &&
              data.data.shareToken
            ) {
              useTicketStore.getState().loadConversations();
              useRouterStore.getState().pushPage("conversation", {
                shareToken: data.data.shareToken,
              });
              CommunicationManager.getInstance().sendMessage({
                name: "cleanup-drawings",
              });
            } else {
              this.sendMessage({
                name: "close-widget",
              });
              useRouterStore.getState().setPage("menu");
              useRouterStore.setState({ showBackButton: true });
            }
            useFormStore.getState().clearFeedbackFlowOptions();
          }, formSentTimeout);
        }
        if (data.name === "feedback-sending-failed" && !isLegacySurveyAck) {
          useFormStore.setState({
            formSent: false,
            sendingForm: false,
            formError: data.data,
          });
        }
        if (data.name === "set-form-data") {
          useFormStore
            .getState()
            .setFormData(data.data.formKey, data.data.data);
        }
        this.processAction(data);
      } catch (exp) {}
    });
  }
}
